import React from "react";
import { MdFolder, MdDragIndicator, MdMoreVert, MdEdit, MdLock, MdLockOpen } from "react-icons/md";
import type { CourseFolder } from "services/instructorCourseFoldersService";
import InlineRename from "components/ui/InlineRename";

type Props = {
  folder: CourseFolder;
  onOpen: (folder: CourseFolder) => void;
  onRename: (folder: CourseFolder, nextTitle: string) => Promise<void> | void;
  onOpenMenu: (folder: CourseFolder, anchor: HTMLElement) => void;
  onToggleAccess: (folder: CourseFolder) => Promise<void> | void;
  dropRef?: React.RefCallback<HTMLDivElement>;
  dropActive?: boolean;
  dragHandleProps?: React.HTMLAttributes<HTMLButtonElement>;
  dragging?: boolean;
};

export default function FolderCard({
  folder,
  onOpen,
  onRename,
  onOpenMenu,
  onToggleAccess,
  dropRef,
  dropActive,
  dragHandleProps,
  dragging,
}: Props) {
  const isLocked = folder.is_locked !== false;
  return (
    <div
      className={`group flex items-center gap-2 rounded-xl border bg-white p-2.5 shadow-sm transition-shadow dark:bg-navy-800 ${
        dragging ? "border-brand-400 shadow-lg" : "border-gray-200 dark:border-white/10"
      }`}
    >
      <button
        type="button"
        {...(dragHandleProps || {})}
        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-gray-300 opacity-60 transition-opacity hover:bg-gray-100 hover:text-gray-500 group-hover:opacity-100 focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 active:cursor-grabbing dark:text-gray-500 dark:hover:bg-white/10 dark:hover:text-gray-300 ${
          dragHandleProps ? "cursor-grab" : "cursor-default"
        }`}
        aria-label="Drag"
      >
        <MdDragIndicator className="h-4 w-4" />
      </button>

      <div
        ref={dropRef}
        className={`-m-2 rounded-xl p-2 ${dropActive ? "ring-2 ring-brand-400" : ""}`.trim()}
        title="Drop here to move into folder"
      >
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-200">
          <MdFolder className="h-5 w-5" />
        </div>
      </div>

      <div className="min-w-0 flex-1">
        <InlineRename
          value={folder.title}
          onSubmit={async (next) => {
            await onRename(folder, next);
          }}
        >
          {({ edit, label }) => (
            <div className="flex min-w-0 items-center gap-1">
              <button
                type="button"
                onClick={() => onOpen(folder)}
                className="block min-w-0 flex-1 truncate text-start text-sm font-semibold text-gray-800 dark:text-white"
                title={label}
              >
                {label}
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  edit();
                }}
                className="rounded-md p-1 text-gray-400 opacity-0 transition-opacity hover:bg-gray-100 hover:text-gray-700 group-hover:opacity-100 dark:hover:bg-white/10 dark:hover:text-white"
                aria-label="Edit"
                title="Edit"
              >
                <MdEdit className="h-4 w-4" />
              </button>
            </div>
          )}
        </InlineRename>
        <p className="text-[11px] uppercase tracking-wide text-gray-400">folder</p>
      </div>

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          void onToggleAccess(folder);
        }}
        className={`flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold transition-colors ${
          isLocked
            ? "text-amber-500 hover:bg-amber-50 dark:text-amber-300 dark:hover:bg-amber-500/10"
            : "text-emerald-600 hover:bg-emerald-50 dark:text-emerald-300 dark:hover:bg-emerald-500/10"
        }`}
        aria-label={isLocked ? "فتح المجلد للمشاهدة" : "قفل المجلد للمشاهدة"}
        title={isLocked ? "مغلق للمشاهدة" : "مفتوح للمشاهدة"}
      >
        {isLocked ? <MdLock className="h-4 w-4" /> : <MdLockOpen className="h-4 w-4" />}
        <span className="hidden sm:inline">{isLocked ? "مغلق" : "مفتوح"}</span>
      </button>

      <button
        type="button"
        onClick={(e) => onOpenMenu(folder, e.currentTarget)}
        className="rounded-md p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-white/10"
        aria-label="More"
      >
        <MdMoreVert className="h-5 w-5" />
      </button>
    </div>
  );
}
